import { api } from '@/shared/services/api';

/** Produs din comanda eMAG (jsonb sincronizat — subset afișat în detalii). */
export interface MarketplaceOrderProduct {
  name?: string | null;
  part_number?: string | null;
  ext_part_number?: string | null;
  part_number_key?: string | null; // PNK
  quantity?: number;
  sale_price?: string | number | null; // NET (preț vânzare)
  original_price?: string | number | null; // NET (preț de bază, înainte de reduceri)
  vat?: string | number | null; // fracție ("0.2100")
  status?: number; // 0 = anulat per linie
  product_voucher_split?: Array<{
    value?: string | number | null;
    vat?: string | number | null;
    voucher_name?: string | null;
    offered_by?: string | null;
  }>;
}

export interface MarketplaceOrderBilling {
  billing_name?: string | null;
  name?: string | null;
  company?: string | null;
  code?: string | null; // CUI
  registration_number?: string | null;
  bank?: string | null;
  iban?: string | null;
  email?: string | null;
  phone?: string | null;
  phone_1?: string | null; // READ-ul real folosește phone_1
  billing_phone?: string | null;
  billing_street?: string | null;
  billing_city?: string | null;
  billing_suburb?: string | null;
  billing_postal_code?: string | null;
  // livrarea — aceleași date pe care le vede sellerul în portalul eMAG
  shipping_contact?: string | null;
  shipping_phone?: string | null;
  shipping_street?: string | null;
  shipping_city?: string | null;
  shipping_suburb?: string | null;
  shipping_postal_code?: string | null;
  // AWB Trendyol (read-only, generat de curier) — capturat la sync în billing
  _sf_cargoTrackingNumber?: string | null;
  _sf_cargoProviderName?: string | null;
  _sf_cargoSenderNumber?: string | null;
}

/** Comanda eMAG din ledger (GET /marketplace/orders). */
export interface MarketplaceOrder {
  id: string;
  source: string;
  externalOrderId: string;
  // 0=anulată, 1=nouă, 2=în procesare, 3=pregătită, 4=finalizată, 5=returnată
  status: number;
  type?: number | null; // 2 = FBE, 3 = FBS
  legalEntity: number; // 0 = PF, 1 = PJ
  customerName?: string | null;
  billing?: MarketplaceOrderBilling | null;
  products?: MarketplaceOrderProduct[] | null;
  vouchers?: Array<{ sale_price?: string | number | null; vat?: string | number | null; voucher_name?: string | null }> | null;
  shippingVoucherSplit?: Array<{ value?: string | number | null }> | null;
  shippingTax?: string | number | null; // CU TVA inclus (ce plătește clientul)
  totalNet: string;
  totalWithVat: string;
  currency: string;
  paymentModeId?: number | null;
  /** rambursul de la livrare calculat de server: factura + transportul eMAG sub 24,99 (0 la plata online) */
  cashOnDelivery?: number | null;
  deliveryMode?: string | null; // courier | pickup (locker)
  lockerId?: string | null; // ID-ul easybox/locker (eMAG) — pt. prefill AWB
  lockerName?: string | null;
  orderDate?: string | null;
  invoiceId?: string | null;
  invoiceExternal: boolean;
  invoiceAttachedAt?: string | null;
  attachError?: string | null;
  invoiceLabel?: string | null;
  invoiceStatus?: string | null;
  // AWB eMAG (generat la platformă, sincronizat nativ) — prin Shipment-ul legat de factură
  awbNumber?: string | null;
  awbStatus?: string | null;
  awbShipmentId?: string | null;
}

/** Cont de curier configurat la platformă (eMAG) — pt. selectorul din dialogul AWB.
 *  Câmpurile suplimentare sunt opționale (general-valabile) — selectorul le folosește pt. afișare clară. */
export interface MarketplaceCourierAccount {
  id: number | string;
  name?: string | null;
  courier?: string | null;
  displayName?: string | null;
  shortLabel?: string | null;
  capability?: string[];
  isLocker?: boolean;
}

export interface GenerateMarketplaceAwbInput {
  courierAccountId: number | string;
  parcelCount?: number;
  weight?: number;
  lockerId?: string;
  cod?: number;
  insuredValue?: number;
  observation?: string;
}

export interface MarketplaceOrdersPage {
  data: MarketplaceOrder[];
  total: number;
  page: number;
  limit: number;
  statusCounts: Record<string, number>;
  /** SKU → produsul din catalog (mapare explicită sau match pe cod). */
  productMap?: Record<string, { productId: string; name: string; via: 'mapping' | 'code' }>;
}

export interface MarketplaceSettings {
  connected: boolean;
  // eMAG / Fashion Days
  apiUser: string | null;
  apiPasswordSet: boolean;
  // Trendyol (credențiale 3-câmpuri + comutator mediu de test)
  sellerId?: string | null;
  apiKey?: string | null;
  apiSecretSet?: boolean;
  sandbox?: boolean;
  // Trendyol — token de panou pt. descărcarea etichetei AWB
  panelTokenSet?: boolean;
  panelTokenExpiresAt?: string | null;
  settings: {
    autoInvoice: boolean;
    autoAcknowledge: boolean;
    attachInvoice: boolean;
    autoStornoReturns: boolean;
    autoStornoCancelled: boolean;
    autoConfirmCancellations: boolean;
    autoSyncStock: boolean;
    seriesId: string | null;
    syncedAt: string | null;
    returnsSyncedAt: string | null;
    defaultCourierHome: string | null;
    defaultCourierLocker: string | null;
  };
  series: Array<{ id: string; prefix: string; isDefault: boolean }>;
}

export interface UpdateMarketplaceSettingsInput {
  // eMAG / Fashion Days
  apiUser?: string;
  apiPassword?: string;
  // Trendyol
  sellerId?: string;
  apiKey?: string;
  apiSecret?: string;
  panelRefreshToken?: string;
  sandbox?: boolean;
  autoInvoice?: boolean;
  autoAcknowledge?: boolean;
  attachInvoice?: boolean;
  autoStornoReturns?: boolean;
  autoStornoCancelled?: boolean;
  autoConfirmCancellations?: boolean;
  autoSyncStock?: boolean;
  seriesId?: string;
  defaultCourierHome?: string;
  defaultCourierLocker?: string;
}

/** Produs returnat dintr-o cerere RMA (jsonb). */
export interface MarketplaceReturnProduct {
  id?: number;
  product_id?: number; // SKU seller
  product_name?: string | null;
  quantity?: number;
  return_reason?: number;
  refund_value?: string | number | null;
  diagnostic?: string | null; // verdictul eMAG după analiza produsului returnat
  reject_reason?: string | null; // motivul respingerii returului (când e cazul)
}

/** Retur eMAG (RMA) din ledger (GET /marketplace/returns). */
export interface MarketplaceReturn {
  id: string;
  externalReturnId: string;
  externalOrderId: string;
  // 1 Incomplet, 2 Nou, 3 Confirmat, 4 Refuzat, 5 Anulat, 6 Primit, 7 Finalizat
  requestStatus: number;
  // 1-2 înlocuire, 3 refund, 4 anulare plată, 5 voucher
  returnType?: number | null;
  products?: MarketplaceReturnProduct[] | null;
  refundValue: string;
  invoiceNumberEmag?: string | null;
  orderInvoiceId?: string | null;
  stornoInvoiceId?: string | null;
  stornoError?: string | null;
  status: string; // pending | stornat | review | ignored
  syncedAt?: string | null;
  orderInvoiceLabel?: string | null;
  stornoInvoiceLabel?: string | null;
  // extrase din payload-ul eMAG pentru afișare (vezi listReturns)
  customerName?: string | null;
  returnDate?: string | null;
  customerNote?: string | null;
}

export interface MarketplaceReturnsPage {
  data: MarketplaceReturn[];
  total: number;
  page: number;
  limit: number;
  statusCounts: Record<string, number>;
  requestStatusCounts?: Record<string, number>;
}

export interface MarketplaceReturnsFilters {
  status?: string;
  requestStatus?: number;
  search?: string;
  page?: number;
  limit?: number;
  platform?: string;
}

export interface MarketplaceOrdersFilters {
  status?: string;
  invoiced?: 'yes' | 'no' | 'external' | '';
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
  platform?: string;
}

const qs = (filters: MarketplaceOrdersFilters) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
};

// `?platform=` selectează marketplace-ul (eMAG / Fashion Days). invoiceOrder/stornoReturn
// NU primesc platform — serverul o derivă din rândul comenzii/returului.
export const marketplaceApi = {
  orders: (platform: string, filters: MarketplaceOrdersFilters = {}) =>
    api.get<MarketplaceOrdersPage>(`/marketplace/orders${qs({ ...filters, platform })}`),
  sync: (platform: string) =>
    api.post<{ orders?: number; invoiced?: number; errors?: string[]; async?: boolean }>(`/marketplace/orders/sync?platform=${platform}`, {}),
  syncStock: (platform: string) =>
    api.post<{ pushed: number; skipped: number; unresolved: number; errors: string[] }>(`/marketplace/orders/sync-stock?platform=${platform}`, {}),
  invoiceOrder: (id: string) =>
    api.post<{ invoiceId: string; label: string; attached: boolean; attachError?: string }>(
      `/marketplace/orders/${id}/invoice`,
      {},
    ),
  // toate platformele odată: cod platformă → comenzi care așteaptă factură (lipsesc cele cu 0)
  uninvoicedCount: () => api.get<{ counts: Record<string, number> }>('/marketplace/orders/uninvoiced-count'),
  settings: (platform: string) => api.get<MarketplaceSettings>(`/marketplace/settings?platform=${platform}`),
  updateSettings: (platform: string, input: UpdateMarketplaceSettingsInput) =>
    api.patch<{ ok: boolean; connected?: boolean; apiPasswordSet?: boolean; apiSecretSet?: boolean }>(
      `/marketplace/settings?platform=${platform}`,
      input,
    ),
  testConnection: (platform: string) => api.post<{ ok: boolean; message: string }>(`/marketplace/test-connection?platform=${platform}`, {}),
  setProductMapping: (platform: string, externalCode: string, productId: string | null) =>
    api.patch<{ ok: boolean }>(`/marketplace/product-mappings?platform=${platform}`, { externalCode, productId }),
  searchProducts: (search: string) =>
    api.get<{ data: Array<{ id: string; name: string; code?: string | null }> }>(
      `/products?search=${encodeURIComponent(search)}&limit=8`,
    ),
  returns: (platform: string, filters: MarketplaceReturnsFilters = {}) =>
    api.get<MarketplaceReturnsPage>(`/marketplace/returns${qs({ ...filters, platform })}`),
  stornoReturn: (id: string) =>
    api.post<{ status: 'stornat' | 'review'; stornoInvoiceId?: string; message?: string }>(
      `/marketplace/returns/${id}/storno`,
      {},
    ),
  stornoOrder: (id: string) =>
    api.post<{ stornoInvoiceId: string; label: string }>(`/marketplace/orders/${id}/storno`, {}),
  courierAccounts: (platform: string) =>
    api.get<{ data: MarketplaceCourierAccount[] }>(`/marketplace/courier-accounts?platform=${platform}`),
  generateAwb: (id: string, input: GenerateMarketplaceAwbInput) =>
    api.post<{ shipmentId: string; awbNumber: string }>(`/marketplace/orders/${id}/awb`, input),
  refreshAwb: (shipmentId: string) =>
    api.post<{ status: string }>(`/marketplace/shipments/${shipmentId}/awb/refresh`, {}),
  cancelAwb: (shipmentId: string) =>
    api.post<{ status: 'cancelled'; message?: string }>(`/marketplace/shipments/${shipmentId}/awb/cancel`, {}),
};
